import { styled } from "../../../../styles";
import { FilterButton, FilterInput, FilterLabel } from "../AdminPage.styles";

/**
 * Styles shared by every audit-log surface: the embeddable AuditLogPanel
 * (EN-5147, migrated from Retool's `moduleAuditLogs`) and the standalone
 * Audit Logs page (`/admin/settings/audit-logs`, EN-4985). Generic admin
 * chrome (page/table/pagination/modal shells) lives in AdminPage.styles.ts —
 * only audit-specific pieces belong here.
 */

export const OperationBadge = styled("span", {
  display: "inline-flex",
  alignItems: "center",
  padding: "4px 10px",
  borderRadius: "999px",
  fontSize: "11px",
  fontWeight: "600",
  letterSpacing: "0.04em",
  textTransform: "uppercase",
  fontFamily: "$openSans",

  variants: {
    operation: {
      INSERT: {
        backgroundColor: "$success-lightest",
        color: "$success-dark",
      },
      UPDATE: {
        backgroundColor: "$primary-20",
        color: "$primary-70",
      },
      DELETE: {
        backgroundColor: "$error-lightest",
        color: "$error-dark",
      },
      OTHER: {
        backgroundColor: "$gray-20",
        color: "$gray-80",
      },
    },
  },

  defaultVariants: {
    operation: "OTHER",
  },
});

export const ChangeSummaryContainer = styled("div", {
  display: "flex",
  flexDirection: "column",
  gap: "8px",
});

export const ChangeSummaryCaption = styled("p", {
  margin: 0,
  fontSize: "13px",
  color: "$gray-80",
  fontFamily: "$openSans",
});

export const ChangeTable = styled("div", {
  display: "grid",
  gridTemplateColumns: "minmax(140px, 1fr) minmax(0, 2fr) minmax(0, 2fr)",
  border: "1px solid $gray-30",
  borderRadius: "6px",
  overflow: "hidden",
  fontFamily: "$openSans",

  variants: {
    layout: {
      diff: {
        gridTemplateColumns: "minmax(140px, 1fr) minmax(0, 2fr) minmax(0, 2fr)",
      },
      values: {
        gridTemplateColumns: "minmax(140px, 1fr) minmax(0, 4fr)",
      },
    },
  },

  defaultVariants: {
    layout: "diff",
  },
});

export const ChangeTableHeader = styled("div", {
  padding: "8px 12px",
  fontSize: "11px",
  fontWeight: "600",
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  color: "$gray-80",
  backgroundColor: "$gray-20",
  borderBottom: "1px solid $gray-30",
});

export const ChangeTableCell = styled("div", {
  padding: "10px 12px",
  fontSize: "13px",
  color: "$charcoal",
  borderBottom: "1px solid $gray-30",
  wordBreak: "break-word",
  whiteSpace: "pre-wrap",

  variants: {
    tone: {
      field: {
        fontWeight: "500",
        backgroundColor: "$white",
      },
      neutral: {
        backgroundColor: "$white",
        fontFamily: "monospace",
        fontSize: "12px",
      },
      before: {
        backgroundColor: "$error-lightest",
        fontFamily: "monospace",
        fontSize: "12px",
      },
      after: {
        backgroundColor: "$success-lightest",
        fontFamily: "monospace",
        fontSize: "12px",
      },
    },
  },

  defaultVariants: {
    tone: "neutral",
  },
});

export const NullValue = styled("span", {
  color: "$gray-60",
  fontStyle: "italic",
});

export const RawJsonDisclosure = styled("details", {
  marginTop: "12px",
  fontFamily: "$openSans",

  "& > summary": {
    cursor: "pointer",
    fontSize: "12px",
    color: "$gray-80",
    padding: "4px 0",
    userSelect: "none",
  },

  "& > summary:hover": {
    color: "$charcoal",
  },
});

export const EmptyChangeNotice = styled("p", {
  margin: 0,
  padding: "12px",
  fontSize: "13px",
  color: "$gray-80",
  backgroundColor: "$gray-20",
  borderRadius: "6px",
  fontFamily: "$openSans",
});

/* Audit filter bars (Audit Logs page and History tabs, EN-5288): every field at
   the date picker's height, so a row of mixed fields lines up. */

export const AUDIT_FILTER_FIELD_HEIGHT = 42;

export const AuditFiltersCard = styled("div", {
  display: "flex",
  flexDirection: "column",
  gap: "20px",
  padding: "20px 24px",
  backgroundColor: "$white",
  borderRadius: "8px",
  border: "1px solid $gray-30",
});

export const AuditFilterLabel = styled(FilterLabel, {
  fontSize: "13px",
});

export const AuditFilterInput = styled(FilterInput, {
  height: `${AUDIT_FILTER_FIELD_HEIGHT}px`,
  fontSize: "15px",
  minWidth: 0,
});

export const AuditFilterButton = styled(FilterButton, {
  height: `${AUDIT_FILTER_FIELD_HEIGHT}px`,
  fontSize: "15px",
});

export const AuditFiltersFooter = styled("div", {
  display: "flex",
  flexWrap: "wrap",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "16px",
});

export const AuditFiltersActions = styled("div", {
  display: "flex",
  alignItems: "center",
  gap: "12px",
  marginLeft: "auto",
});

export const AuditFiltersHint = styled("p", {
  margin: 0,
  fontSize: "13px",
  fontFamily: "$openSans",
  color: "$gray-80",

  variants: {
    error: {
      true: {
        color: "$error-light",
      },
    },
  },
});

export const AuditFiltersRow = styled("div", {
  display: "flex",
  flexWrap: "wrap",
  alignItems: "flex-end",
  gap: "16px",
});

/** A field in `AuditFiltersRow`: grows to share the row, never narrower than it can read. */
export const AuditFilterField = styled("div", {
  display: "flex",
  flexDirection: "column",
  gap: "6px",
  flex: "1 1 160px",
  minWidth: "160px",
});

export const AuditWindowNav = styled("div", {
  display: "flex",
  gap: "8px",
});
